import { useState } from 'react';
import { Slider } from '@mui/material';

// Vrije schuifbalk (Visual Analog Scale) met alleen tekst aan de uiteinden.
//
// Nodig omdat er aan de invulkant nog geen schuifbalk bestond. Een dun omhulsel
// om MUI `Slider`, dat toetsenbord (pijltjes) en schermlezer al regelt.
//
// GEEN BEGINPOSITIE. Een knop die vanaf het begin in het midden staat, is een
// antwoord dat niemand gaf — en hij trekt het antwoord naar zich toe. Zolang
// `value` null is, is de knop een hol rondje (CSS: `.vas-untouched`) en meldt de
// balk zich als 'nog niet beantwoord'. De knop staat dan wel op 50, want MUI
// heeft een plek nodig om vandaan te bewegen met het toetsenbord.
//
// TIJDENS HET SLEPEN BLIJFT DE PLEK HIER. `onChange` gaat pas naar buiten bij
// loslaten (`onChangeCommitted`; MUI geeft die ook bij de pijltjestoetsen). Elke
// beweging doorgeven schreef naar de gedeelde antwoorden, en dan tekende de hele
// vragenlijst ~60× per seconde opnieuw (/complete 2026-10-14).
// AI-CONTEXT: See docs/features/vas-slider.md

const VAS_MIN = 0;
const VAS_MAX = 100;
const RESTING_POSITION = 50;

/**
 * @param {object} props
 * @param {number|null} props.value - Plek op de balk (0–100), of null zolang de invuller hem niet aanraakte
 * @param {(position: number) => void} props.onChange - Krijgt de nieuwe plek bij elke beweging
 * @param {string} props.left - Tekst bij de laagste waarde
 * @param {string} props.right - Tekst bij de hoogste waarde
 * @param {string} props.ariaLabel - Waar deze balk over gaat, voor een schermlezer
 * @param {string} props.untouchedText - Wat een schermlezer hoort zolang er geen antwoord is
 */
const VasSlider = ({ value, onChange, left, right, ariaLabel, untouchedText }) => {
    // De plek tijdens het slepen; null zodra er losgelaten is.
    const [dragPosition, setDragPosition] = useState(/** @type {number | null} */ (null));

    const answered = value !== null && value !== undefined;
    const untouched = !answered && dragPosition === null;
    const shownPosition = dragPosition ?? (answered ? value : RESTING_POSITION);

    return (
        <div className={`vas-slider${untouched ? ' vas-untouched' : ''}`} data-testid='vas-slider'>
            <Slider
                value={shownPosition}
                min={VAS_MIN}
                max={VAS_MAX}
                step={1}
                track={false}
                aria-label={ariaLabel}
                getAriaValueText={(position) => (untouched ? untouchedText : String(position))}
                onChange={(_event, position) => setDragPosition(Number(position))}
                // Loslaten telt altijd, ook als de plek gelijk bleef: het holle
                // rondje staat op 50, en wie precies het midden kiest, gaf anders
                // geen antwoord (MUI meldt dan geen `onChange`).
                onChangeCommitted={(_event, position) => {
                    setDragPosition(null);
                    onChange(Number(position));
                }}
            />
            <div className='vas-anchors'>
                <span className='vas-anchor'>{left}</span>
                <span className='vas-anchor'>{right}</span>
            </div>
        </div>
    );
};

export default VasSlider;
